import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, Mail, Store, Eye, EyeOff, CheckCircle, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeAuthErrorMessage } from '../utils/authErrors';
import { Button, Input } from '../components/ui';

const ResetPassword: React.FC = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { logout } = useAuth();
  const clearedSessionRef = useRef(false);

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const stateEmail = (location.state as { email?: string } | null)?.email ?? '';
    const paramEmail = searchParams.get('email') ?? '';
    const paramCode = searchParams.get('code') ?? '';
    if (stateEmail) setEmail(stateEmail);
    else if (paramEmail) setEmail(decodeURIComponent(paramEmail));
    if (paramCode) setCode(paramCode.trim());

    if ((paramEmail || stateEmail) && !clearedSessionRef.current) {
      clearedSessionRef.current = true;
      logout();
    }
  }, [location.state, searchParams, logout]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => navigate('/login', { replace: true }), 2500);
    return () => clearTimeout(timer);
  }, [success, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError(t('resetPassword.errors.passwordTooShort'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('resetPassword.errors.passwordsNotMatch'));
      return;
    }
    if (!code.trim()) {
      setError(t('resetPassword.errors.codeRequired'));
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/reset-password', {
        email: email.trim(),
        code: code.trim(),
        password,
      });
      if (response.data?.status === 'success') {
        setSuccess(true);
      } else {
        setError(response.data?.message || t('resetPassword.errors.failed'));
      }
    } catch (err: unknown) {
      const raw =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message;
      setError(sanitizeAuthErrorMessage(raw) || t('resetPassword.errors.failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-orange-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            {success ? (
              <CheckCircle className="text-green-500" size={48} />
            ) : (
              <Store className="text-custom-primary" size={48} />
            )}
          </div>
          <h1 className="text-4xl font-bold text-custom-primary mb-2">
            {success ? t('resetPassword.success.title') : t('resetPassword.title')}
          </h1>
          <p className="text-gray-600">
            {success ? t('resetPassword.success.message') : t('resetPassword.subtitle')}
          </p>
        </div>

        <div className="card p-8">
          {success ? (
            <div className="text-center space-y-4">
              <p className="text-gray-600">{t('resetPassword.success.redirecting')}</p>
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-custom-primary mx-auto" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              {error ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              ) : null}

              <Input
                label={t('resetPassword.emailPlaceholder')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('resetPassword.emailPlaceholder')}
                icon={Mail}
                iconPosition="left"
                required
              />

              <Input
                label={t('resetPassword.codePlaceholder')}
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.trim())}
                placeholder={t('resetPassword.codePlaceholder')}
                maxLength={6}
                inputMode="numeric"
                autoComplete="one-time-code"
                required
              />

              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('resetPassword.passwordPlaceholder')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-field pl-12 pr-12"
                    placeholder={t('resetPassword.passwordPlaceholder')}
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('resetPassword.confirmPasswordPlaceholder')}
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input-field pl-12 pr-12"
                    placeholder={t('resetPassword.confirmPasswordPlaceholder')}
                    required
                    minLength={6}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              <div className="space-y-4 pt-1">
                <Button type="submit" variant="primary" className="w-full" loading={loading}>
                  {loading ? t('resetPassword.submitting') : t('resetPassword.submit')}
                </Button>
                <p className="text-center text-sm text-gray-500">
                  <button
                    type="button"
                    onClick={() => navigate('/forgot-password', { state: { email } })}
                    className="text-custom-primary font-medium hover:underline"
                  >
                    {t('resetPassword.requestCode')}
                  </button>
                </p>
              </div>
            </form>
          )}

          <button
            type="button"
            onClick={() => navigate('/login')}
            className="mt-6 flex items-center justify-center gap-2 w-full text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={16} />
            {t('resetPassword.backToLogin')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
